const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: Passive Isolator — Transmissibility vs Frequency ───
function DiagramTransmissibility() {
  const W = 900, H = 370;
  const padL = 58, padR = 18, padT = 36, plotH = 240;

  const nPts = 300;
  // r = ω/ωn from 0.01 to 4
  const rVals = Array.from({ length: nPts }, (_, i) => 0.01 + i / nPts * 3.99);

  const zetas = [
    { z: 0.05, col: '#c0392b', label: 'ζ = 0.05' },
    { z: 0.15, col: '#e07b00', label: 'ζ = 0.15' },
    { z: 0.3,  col: '#2E75B6', label: 'ζ = 0.30' },
    { z: 0.7,  col: '#27ae60', label: 'ζ = 0.70' },
  ];

  // Transmissibility with damping: T = sqrt((1+(2ζr)²)/((1-r²)²+(2ζr)²))
  const T = (r, z) => Math.sqrt((1 + (2*z*r)**2) / ((1 - r*r)**2 + (2*z*r)**2));

  const mapX = r => padL + (r - 0.01) / 3.99 * (W - padL - padR);
  const mapY = db => padT + plotH - Math.min(Math.max((db + 40) / 80 * plotH, 0), plotH);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Passive Isolation Transmissibility — T(r, ζ) vs. Frequency Ratio r = ω/ωₙ</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="tr1" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#888" />
          </marker>
        </defs>

        {/* Grid */}
        {[-40, -20, -10, 0, 10, 20, 30].map(db => {
          const y = mapY(db);
          return (
            <g key={db}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke={db === 0 ? '#ddd' : '#f5f5f5'} strokeWidth={db === 0 ? 1.5 : 1} />
              <text x={padL - 4} y={y + 4} textAnchor="end" fontSize="9.5" fill="#aaa">{db} dB</text>
            </g>
          );
        })}
        {[0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4].map(r => {
          const x = mapX(r);
          return (
            <g key={r}>
              <line x1={x} y1={padT} x2={x} y2={padT + plotH} stroke="#f0f0f0" strokeWidth={r === 1 ? 1.5 : 1} />
              <text x={x} y={padT + plotH + 14} textAnchor="middle" fontSize="9.5" fill="#aaa">{r}</text>
            </g>
          );
        })}

        {/* T = 1 (0 dB) reference line */}
        <line x1={padL} y1={mapY(0)} x2={W - padR} y2={mapY(0)} stroke="#c0392b" strokeWidth="1" strokeDasharray="6,4" opacity="0.4" />

        {/* √2 crossover line at r = √2 */}
        {(() => {
          const x = mapX(Math.sqrt(2));
          return (
            <>
              <line x1={x} y1={padT} x2={x} y2={padT + plotH} stroke="#9b59b6" strokeWidth="1.5" strokeDasharray="5,3" />
              <text x={x + 3} y={padT + 14} fontSize="9.5" fill="#9b59b6" fontWeight="700">r = √2</text>
              <text x={x + 3} y={padT + 27} fontSize="9" fill="#9b59b6">isolation begins</text>
            </>
          );
        })()}

        {/* Curves */}
        {zetas.map(({ z, col, label }) => {
          const pts = rVals.map(r => {
            const db = 20 * Math.log10(Math.max(T(r, z), 0.001));
            return `${mapX(r)},${mapY(db)}`;
          }).join(' ');
          return <polyline key={z} points={pts} fill="none" stroke={col} strokeWidth="2.2" />;
        })}

        {/* Axes */}
        <line x1={padL} y1={padT} x2={padL} y2={padT + plotH + 4} stroke="#888" strokeWidth="1.5" />
        <line x1={padL} y1={padT + plotH} x2={W - padR} y2={padT + plotH} stroke="#888" strokeWidth="1.5" markerEnd="url(#tr1)" />

        {/* Labels */}
        <text x={W / 2} y={padT + plotH + 28} textAnchor="middle" fontSize="10.5" fill="#555">Frequency Ratio  r = ω / ωₙ</text>
        <text x={14} y={padT + plotH / 2} fontSize="10" fill="#555" transform={`rotate(-90,14,${padT + plotH / 2})`} textAnchor="middle">Transmissibility (dB)</text>

        {/* Key annotations */}
        {(() => {
          const rx = mapX(1), peakY = mapY(20 * Math.log10(T(1, 0.05)));
          return (
            <>
              <circle cx={rx} cy={peakY} r={4} fill="#c0392b" />
              <line x1={rx} y1={peakY} x2={rx + 60} y2={peakY - 10} stroke="#c0392b" strokeWidth="1" strokeDasharray="3,2" />
              <text x={rx + 62} y={peakY - 12} fontSize="9.5" fill="#c0392b" fontWeight="700">Resonance (r=1)</text>
              <text x={rx + 62} y={peakY - 1} fontSize="9" fill="#c0392b">T = 1/(2ζ) at peak</text>
            </>
          );
        })()}

        {/* Amplification / isolation zone labels */}
        <rect x={padL + 6} y={padT + 4} width={90} height={20} rx={2} fill="#fff0f0" stroke="#c0392b" strokeWidth="1" />
        <text x={padL + 10} y={padT + 17} fontSize="9.5" fill="#c0392b" fontWeight="700">AMPLIFICATION (r &lt; √2)</text>
        <rect x={mapX(Math.sqrt(2)) + 6} y={padT + 4} width={86} height={20} rx={2} fill="#f0fff4" stroke="#27ae60" strokeWidth="1" />
        <text x={mapX(Math.sqrt(2)) + 10} y={padT + 17} fontSize="9.5" fill="#27ae60" fontWeight="700">ISOLATION (r &gt; √2)</text>

        {/* Legend */}
        {zetas.map(({ col, label }, i) => (
          <g key={i}>
            <line x1={W - padR - 120} y1={padT + 50 + i * 22} x2={W - padR - 96} y2={padT + 50 + i * 22} stroke={col} strokeWidth="2.2" />
            <text x={W - padR - 92} y={padT + 54 + i * 22} fontSize="10" fill={col} fontWeight="700">{label}</text>
          </g>
        ))}

        {/* Isolation slope annotation */}
        {(() => {
          const r1 = 2.5, r2 = 3.8, z = 0.05;
          const x1 = mapX(r1), x2 = mapX(r2);
          const y1 = mapY(20 * Math.log10(T(r1, z)));
          const y2 = mapY(20 * Math.log10(T(r2, z)));
          return (
            <>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#555" strokeWidth="1" strokeDasharray="3,2" />
              <text x={(x1+x2)/2 + 10} y={(y1+y2)/2} fontSize="9" fill="#555">−40 dB/dec (undamped)</text>
            </>
          );
        })()}

        <text x={W / 2} y={H - 4} textAnchor="middle" fontSize="10" fill="#777">T = √[(1+(2ζr)²) / ((1−r²)²+(2ζr)²)]  |  Isolation effective for r &gt; √2 ≈ 1.41</text>
      </svg>
      <div style={S.dCaption}>Transmissibility T measures how much vibration passes through an isolator. Below r = √2 the isolator amplifies input motion; above r = √2 it attenuates. At resonance (r = 1) the peak amplitude is 1/(2ζ) — high damping controls the resonance peak but degrades high-frequency isolation. The fundamental trade-off of passive isolation: more damping reduces resonance amplification but worsens isolation in the r ≫ √2 region. The ideal passive isolator has low damping and a very low natural frequency (ωₙ ≪ ω_excitation).</div>
    </div>
  );
}

// ── Diagram 2: SDOF Isolator Physical Model ───────────────────────
function DiagramIsolatorModel() {
  const W = 900, H = 310;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Passive Isolator Physical Models — Force and Motion Transmissibility</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="pm1" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c0392b" />
          </marker>
          <marker id="pm2" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#2E75B6" />
          </marker>
        </defs>

        {/* ── Panel 1: Force isolation (machine on isolator) ── */}
        <rect x={10} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={10 + PW / 2} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Force Isolation — Machine on Mounts</text>
        <text x={10 + PW / 2} y={43} textAnchor="middle" fontSize="10" fill="#555">Goal: reduce force transmitted to foundation</text>

        {(() => {
          const cx = 10 + PW / 2;
          // Machine block
          const mX = cx - 60, mY = 60, mW = 120, mH = 55;
          // Spring-damper
          const sX1 = cx - 45, sX2 = cx + 45;
          const sYtop = mY + mH, sYbot = 210;
          // Floor
          const fY = 230;

          // Draw spring (zigzag)
          const springPts = (x, y1, y2) => {
            const n = 5, pts = [];
            pts.push(`${x},${y1}`);
            for (let i = 0; i < n; i++) {
              const yy = y1 + (y2 - y1) * (i + 0.3) / n;
              pts.push(`${x + (i % 2 === 0 ? 10 : -10)},${yy}`);
            }
            pts.push(`${x},${y2}`);
            return pts.join(' ');
          };

          return (
            <>
              {/* Machine block */}
              <rect x={mX} y={mY} width={mW} height={mH} rx={5} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2.5" />
              <text x={cx} y={mY + 28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Machine  m</text>
              <text x={cx} y={mY + 44} textAnchor="middle" fontSize="11" fill="#555">F(t) = F₀ sin(ωt)</text>

              {/* Force arrow (internal) */}
              <line x1={cx} y1={mY - 2} x2={cx} y2={mY + 20} stroke="#c0392b" strokeWidth="2" markerEnd="url(#pm1)" />
              <text x={cx + 6} y={mY - 4} fontSize="9.5" fill="#c0392b">F(t)</text>

              {/* Spring (left) */}
              <polyline points={springPts(sX1, sYtop, sYbot)} fill="none" stroke="#2E75B6" strokeWidth="2" />
              <text x={sX1 - 16} y={(sYtop + sYbot) / 2 + 4} fontSize="10" fill="#2E75B6" fontWeight="700">k</text>

              {/* Damper (right): two parallel lines + piston */}
              {(() => {
                const mid = (sYtop + sYbot) / 2;
                return (
                  <>
                    <line x1={sX2} y1={sYtop} x2={sX2} y2={mid - 10} stroke="#e07b00" strokeWidth="2" />
                    <rect x={sX2 - 10} y={mid - 10} width={20} height={16} fill="#fff9e6" stroke="#e07b00" strokeWidth="2" />
                    <line x1={sX2} y1={mid + 6} x2={sX2} y2={sYbot} stroke="#e07b00" strokeWidth="2" />
                    <text x={sX2 + 14} y={(sYtop + sYbot) / 2 + 4} fontSize="10" fill="#e07b00" fontWeight="700">c</text>
                  </>
                );
              })()}

              {/* Floor */}
              <rect x={cx - 80} y={fY} width={160} height={14} rx={2} fill="#888" />
              <text x={cx} y={fY + 28} textAnchor="middle" fontSize="10" fill="#888">Foundation (base)</text>

              {/* FT arrow */}
              <line x1={cx + 70} y1={fY} x2={cx + 70} y2={fY - 30} stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#pm1)" />
              <text x={cx + 74} y={fY - 32} fontSize="9.5" fill="#c0392b" fontWeight="700">Fₜ (transmitted)</text>

              {/* Formula */}
              <rect x={14} y={H - 64} width={PW - 20} height={48} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5" />
              <text x={20} y={H - 50} fontSize="10.5" fill="#7B0D0D" fontWeight="700">Force transmissibility:  T_F = Fₜ/F₀</text>
              <text x={20} y={H - 36} fontSize="10" fill="#333" fontFamily="'Courier New',monospace">T_F = √[(1+(2ζr)²) / ((1−r²)²+(2ζr)²)]</text>
              <text x={20} y={H - 22} fontSize="9.5" fill="#555">Same formula as motion transmissibility — reciprocal system</text>
            </>
          );
        })()}

        {/* ── Panel 2: Motion isolation (sensitive equipment) ── */}
        <rect x={PW + 20} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={PW + 20 + PW / 2} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Motion Isolation — Sensitive Equipment</text>
        <text x={PW + 20 + PW / 2} y={43} textAnchor="middle" fontSize="10" fill="#555">Goal: reduce motion of payload from moving base</text>

        {(() => {
          const cx = PW + 20 + PW / 2;
          const sX1 = cx - 45, sX2 = cx + 45;
          const baseY = 228, baseH = 14;
          const sYbot = baseY, sYtop = 155;
          const pY = 80, pH = 55, pW = 120;

          const springPts = (x, y1, y2) => {
            const n = 5, pts = [];
            pts.push(`${x},${y1}`);
            for (let i = 0; i < n; i++) {
              const yy = y1 + (y2 - y1) * (i + 0.3) / n;
              pts.push(`${x + (i % 2 === 0 ? 10 : -10)},${yy}`);
            }
            pts.push(`${x},${y2}`);
            return pts.join(' ');
          };

          return (
            <>
              {/* Payload */}
              <rect x={cx - pW / 2} y={pY} width={pW} height={pH} rx={5} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="2.5" />
              <text x={cx} y={pY + 26} textAnchor="middle" fontSize="13" fill="#2E75B6" fontWeight="700">Payload  m</text>
              <text x={cx} y={pY + 42} textAnchor="middle" fontSize="10" fill="#555">x(t) = response</text>

              {/* Spring */}
              <polyline points={springPts(sX1, sYtop, pY + pH)} fill="none" stroke="#2E75B6" strokeWidth="2" />
              <text x={sX1 - 14} y={(sYtop + pY + pH) / 2 + 4} fontSize="10" fill="#2E75B6" fontWeight="700">k</text>

              {/* Damper */}
              {(() => {
                const mid = (sYtop + pY + pH) / 2;
                return (
                  <>
                    <line x1={sX2} y1={pY + pH} x2={sX2} y2={mid + 10} stroke="#e07b00" strokeWidth="2" />
                    <rect x={sX2 - 10} y={mid - 6} width={20} height={16} fill="#fff9e6" stroke="#e07b00" strokeWidth="2" />
                    <line x1={sX2} y1={mid - 6} x2={sX2} y2={sYtop} stroke="#e07b00" strokeWidth="2" />
                    <text x={sX2 + 14} y={(sYtop + pY + pH) / 2 + 4} fontSize="10" fill="#e07b00" fontWeight="700">c</text>
                  </>
                );
              })()}

              {/* Moving base */}
              <rect x={cx - 80} y={baseY} width={160} height={baseH} rx={2} fill="#888" />
              {/* Base motion arrow */}
              <line x1={cx + 82} y1={baseY + baseH / 2} x2={cx + 82 + 28} y2={baseY + baseH / 2} stroke="#888" strokeWidth="2" markerEnd="url(#pm2)" />
              <text x={cx + 84} y={baseY - 4} fontSize="9.5" fill="#888">y(t) base motion</text>

              {/* Response x annotation */}
              <line x1={cx - pW / 2 - 16} y1={pY + pH / 2} x2={cx - pW / 2 - 16} y2={pY + pH / 2 + 26} stroke="#2E75B6" strokeWidth="2" markerEnd="url(#pm2)" />
              <text x={cx - pW / 2 - 36} y={pY + pH / 2 + 14} fontSize="9.5" fill="#2E75B6">x(t)</text>

              {/* Formula */}
              <rect x={PW + 24} y={H - 64} width={PW - 12} height={48} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5" />
              <text x={PW + 30} y={H - 50} fontSize="10.5" fill="#2E75B6" fontWeight="700">Motion transmissibility:  T_X = x/y</text>
              <text x={PW + 30} y={H - 36} fontSize="10" fill="#333" fontFamily="'Courier New',monospace">T_X = √[(1+(2ζr)²) / ((1−r²)²+(2ζr)²)]</text>
              <text x={PW + 30} y={H - 22} fontSize="9.5" fill="#555">Design goal: ωₙ ≪ ω_excitation  (low mount stiffness)</text>
            </>
          );
        })()}
      </svg>
      <div style={S.dCaption}>The two canonical isolation problems — force isolation (reducing forces transmitted from a vibrating machine to its foundation) and motion isolation (protecting sensitive equipment from a vibrating base) — share the identical transmissibility formula. The key design parameter is the ratio r = ω/ωₙ: to achieve T &lt; 1 we need r &gt; √2, which means the mount's natural frequency ωₙ must be well below the disturbing frequency ω. Low ωₙ requires soft mounts (low stiffness k = mωₙ²).</div>
    </div>
  );
}

// ── Diagram 3: Isolator Types — Physical Comparison ───────────────
function DiagramIsolatorTypes() {
  const W = 900, H = 280;
  const types = [
    {
      name: 'Rubber/\nElastomer',
      col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b',
      icon: '▭',
      specs: ['f_n: 5–30 Hz', 'ζ: 0.03–0.15', 'k: high', 'Temp sensitive'],
      use: 'Engines, HVAC,\nelectronics mounts',
    },
    {
      name: 'Coil Spring',
      col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6',
      icon: '⌇',
      specs: ['f_n: 1–10 Hz', 'ζ: ~0.005', 'k: low–med', 'Stable, durable'],
      use: 'Precision floors,\nheavy machinery',
    },
    {
      name: 'Air Spring\n(Pneumatic)',
      col: '#27ae60', bg: '#f0fff4', bc: '#27ae60',
      icon: '⬡',
      specs: ['f_n: 0.5–5 Hz', 'ζ: 0.02–0.05', 'k: adjustable', 'Height control'],
      use: 'Optical tables,\nvehicle suspension',
    },
    {
      name: 'Wire Rope\nIsolator',
      col: '#e07b00', bg: '#fff9e6', bc: '#e07b00',
      icon: '∿',
      specs: ['f_n: 5–30 Hz', 'ζ: 0.1–0.2', 'k: nonlinear', 'Shock + vib'],
      use: 'Military equip.,\nportable electronics',
    },
    {
      name: 'Viscous\nFluid Mount',
      col: '#9b59b6', bg: '#f8f0ff', bc: '#9b59b6',
      icon: '≈',
      specs: ['f_n: 3–15 Hz', 'ζ: 0.1–0.5', 'k: freq-dep', 'High damping'],
      use: 'Automotive engine,\nvehicle body mounts',
    },
    {
      name: 'Active\nMount (AVS)',
      col: '#7B0D0D', bg: '#fff5f5', bc: '#c0392b',
      icon: '⚙',
      specs: ['f_n: tunable', 'ζ: controlled', 'k: active', 'Power needed'],
      use: 'Spacecraft, semi-\nconductor fab',
    },
  ];

  const cardW = (W - 20) / 3, cardH = 115;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Isolator Technology Comparison — Six Major Types</div>
      <svg viewBox={`0 0 ${W} 254`} style={{ width: '100%', display: 'block' }}>
        {types.map(({ name, col, bg, bc, icon, specs, use }, i) => {
          const c = i % 3, r = Math.floor(i / 3);
          const x = 8 + c * (cardW + 3), y = 8 + r * (cardH + 6);
          return (
            <g key={i}>
              <rect x={x} y={y} width={cardW} height={cardH} rx={5} fill={bg} stroke={bc} strokeWidth="1.5" />
              <rect x={x} y={y} width={cardW} height={26} rx={4} fill={bc} />
              <text x={x + cardW / 2} y={y + 17} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{name.replace('\n', ' ')}</text>
              <text x={x + cardW - 10} y={y + 20} textAnchor="end" fontSize="18" fill="#fff" opacity="0.35">{icon}</text>
              <text x={x + 8} y={y + 40} fontSize="10.5" fill={col} fontWeight="700">Specs:</text>
              {specs.map((s, si) => (
                <text key={si} x={x + 8} y={y + 53 + si * 13} fontSize="9.5" fill="#333">{s}</text>
              ))}
              <text x={x + 8} y={y + 100} fontSize="9.5" fill={col} fontWeight="700">Uses: {use.replace('\n', ' ')}</text>
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>Isolator selection depends on the required natural frequency, acceptable damping level, operating environment, and load capacity. Coil springs and air springs achieve the lowest natural frequencies (best isolation) but provide very little damping — they are often combined with a separate viscous damper. Elastomeric mounts are compact and self-damped but temperature-sensitive and subject to creep. Active mounts can overcome the fundamental passive trade-off but require power, sensors, and control electronics.</div>
    </div>
  );
}

// ── Diagram 4: Dynamic Vibration Absorber (DVA) ───────────────────
function DiagramDVA() {
  const W = 900, H = 360;
  const PW = (W - 30) / 2;

  // DVA frequency response: primary mass amplitude vs r
  // r goes 0.3 → 2.1
  const nPts = 600;
  const rMin = 0.3, rMax = 2.1;
  const rVals = Array.from({ length: nPts }, (_, i) => rMin + i / (nPts - 1) * (rMax - rMin));
  const mu = 0.05; // mass ratio
  const fOpt = 1 / (1 + mu); // optimal absorber tuning ratio

  // Undamped DVA — exact 2-DOF formula for primary mass X1
  // |X1/Xst| = |ra²-r²| / |(1-r²)(ra²-r²) - mu*ra²*r²|
  // ra = absorber freq ratio = fOpt here (tuned to primary natural freq)
  const ampWithDVA = rVals.map(r => {
    const ra = fOpt;
    const num = Math.abs(ra * ra - r * r);
    const denom = Math.abs((1 - r * r) * (ra * ra - r * r) - mu * ra * ra * r * r);
    // Clamp to 12 for display; true peaks go to infinity
    return Math.min(num / Math.max(denom, 1e-5), 12);
  });

  // Without absorber: SDOF with light damping ζ=0.05
  const ampNoAbs = rVals.map(r => {
    const z = 0.05;
    return Math.min(1 / Math.sqrt((1 - r * r) ** 2 + (2 * z * r) ** 2), 12);
  });

  // Plot coordinate helpers — panel 2 only
  const padL = 52, padR = 14, padT = 40;
  const plotH = 200, plotW = PW - padL - padR - 6;
  const yMax = 12; // amplitude axis max
  // ox, oy are applied inside the IIFE where panel 2 is drawn
  const toX = (r, ox) => ox + (r - rMin) / (rMax - rMin) * plotW;
  const toY = (v, oy) => oy - Math.min(v / yMax, 1) * plotH;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Dynamic Vibration Absorber (DVA / TMD) — Principle and Frequency Response</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="dva1" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#888" />
          </marker>
          <marker id="dva2" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c0392b" />
          </marker>
        </defs>

        {/* Panel 1: Physical model */}
        <rect x={10} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={10 + PW / 2} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">DVA Physical Model — Absorber Attached to Primary</text>

        {(() => {
          const cx = 10 + PW / 2;
          // Primary mass
          const m1Y = 100, m1W = 140, m1H = 50;
          // Absorber mass
          const m2Y = 40, m2W = 80, m2H = 36;
          // Foundation
          const flY = 220;

          const springZ = (x, y1, y2, col) => {
            const n = 5, pts = [`${x},${y1}`];
            for (let i = 0; i < n; i++) {
              const yy = y1 + (y2 - y1) * (i + 0.3) / n;
              pts.push(`${x + (i % 2 === 0 ? 9 : -9)},${yy}`);
            }
            pts.push(`${x},${y2}`);
            return <polyline key={x} points={pts.join(' ')} fill="none" stroke={col} strokeWidth="2" />;
          };

          return (
            <>
              {/* Foundation */}
              <rect x={cx - 90} y={flY} width={180} height={12} rx={2} fill="#999" />
              {/* Hatch marks */}
              {[-70,-50,-30,-10,10,30,50,70].map(dx => (
                <line key={dx} x1={cx + dx} y1={flY + 12} x2={cx + dx - 10} y2={flY + 22} stroke="#bbb" strokeWidth="1" />
              ))}

              {/* Primary spring (left) */}
              {springZ(cx - 40, flY, m1Y + m1H, '#c0392b')}
              <text x={cx - 55} y={(flY + m1Y + m1H) / 2 + 4} fontSize="10" fill="#c0392b" fontWeight="700">k₁</text>

              {/* Primary damper (right) */}
              {(() => {
                const mid = (flY + m1Y + m1H) / 2;
                return (
                  <>
                    <line x1={cx + 40} y1={flY} x2={cx + 40} y2={mid - 8} stroke="#c0392b" strokeWidth="2" />
                    <rect x={cx + 30} y={mid - 8} width={20} height={14} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5" />
                    <line x1={cx + 40} y1={mid + 6} x2={cx + 40} y2={m1Y + m1H} stroke="#c0392b" strokeWidth="2" />
                    <text x={cx + 56} y={(flY + m1Y + m1H) / 2 + 4} fontSize="10" fill="#c0392b" fontWeight="700">c₁</text>
                  </>
                );
              })()}

              {/* Primary mass */}
              <rect x={cx - m1W / 2} y={m1Y} width={m1W} height={m1H} rx={5} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2.5" />
              <text x={cx} y={m1Y + 22} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">M (primary)</text>
              <text x={cx} y={m1Y + 38} textAnchor="middle" fontSize="10" fill="#555">F(t) = F₀ sin(ω₁t)</text>

              {/* Absorber spring */}
              {springZ(cx, m1Y, m2Y + m2H, '#2E75B6')}
              <text x={cx + 14} y={(m1Y + m2Y + m2H) / 2 + 4} fontSize="10" fill="#2E75B6" fontWeight="700">k₂</text>

              {/* Absorber mass */}
              <rect x={cx - m2W / 2} y={m2Y} width={m2W} height={m2H} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="2.5" />
              <text x={cx} y={m2Y + 14} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">m (absorber)</text>
              <text x={cx} y={m2Y + 28} textAnchor="middle" fontSize="10" fill="#555">μ = m/M</text>

              {/* Excitation arrow */}
              <line x1={cx - 90} y1={m1Y + 20} x2={cx - m1W / 2} y2={m1Y + 20} stroke="#c0392b" strokeWidth="2" markerEnd="url(#dva2)" />
              <text x={cx - 96} y={m1Y + 16} textAnchor="end" fontSize="10" fill="#c0392b" fontWeight="700">F₀ sin(ωt)</text>

              {/* Key formulae */}
              <rect x={14} y={H - 96} width={PW - 20} height={80} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5" />
              <text x={20} y={H - 82} fontSize="10.5" fill="#7B0D0D" fontWeight="700">Optimal tuning (Den Hartog):</text>
              <text x={20} y={H - 67} fontSize="10" fill="#333" fontFamily="'Courier New',monospace">f_opt = ω₂/ω₁ = 1/(1+μ)</text>
              <text x={20} y={H - 52} fontSize="10" fill="#333" fontFamily="'Courier New',monospace">ζ_opt = √(3μ/(8(1+μ)³))</text>
              <text x={20} y={H - 38} fontSize="10" fill="#555">At tuned frequency: X₁ = 0 (absorber cancels force)</text>
              <text x={20} y={H - 24} fontSize="10" fill="#555">Two new resonances flank the original: ±√(1±√(μ/(1+μ)))</text>
            </>
          );
        })()}

        {/* Panel 2: Frequency response comparison */}
        <rect x={PW + 20} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={PW + 20 + PW / 2} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Primary Mass Amplitude — With and Without DVA (μ=0.05)</text>

        {(() => {
          // Panel-2 coordinate origin
          const ox = PW + 20 + padL;
          const oy = padT + plotH; // bottom of plot area

          // Split "with DVA" curve at discontinuities (near vertical asymptotes)
          // Build segments separated wherever amplitude hits the clamp (12)
          const segments = [];
          let seg = [];
          for (let i = 0; i < rVals.length; i++) {
            const x = toX(rVals[i], ox);
            const y = toY(ampWithDVA[i], oy);
            // Break segment at discontinuity (clamped value = plotH ceiling or near-zero transition)
            const prev = i > 0 ? ampWithDVA[i - 1] : ampWithDVA[i];
            const bigJump = Math.abs(ampWithDVA[i] - prev) > 3;
            if (bigJump && seg.length > 1) {
              segments.push(seg);
              seg = [];
            }
            if (y >= padT - 4 && y <= oy + 2) seg.push(`${x},${y}`);
          }
          if (seg.length > 1) segments.push(seg);

          // Anti-resonance: minimum of with-DVA curve (near r = fOpt)
          let antiIdx = 0;
          for (let i = 1; i < rVals.length; i++) {
            if (ampWithDVA[i] < ampWithDVA[antiIdx]) antiIdx = i;
          }
          const antiX = toX(rVals[antiIdx], ox);
          const antiY = toY(ampWithDVA[antiIdx], oy);

          return (
            <>
              {/* Horizontal grid lines */}
              {[0, 2, 4, 6, 8, 10, 12].map(v => {
                const y = toY(v, oy);
                return (
                  <g key={v}>
                    <line x1={ox} y1={y} x2={ox + plotW} y2={y} stroke={v === 0 ? '#ccc' : '#f3f3f3'} strokeWidth="1" />
                    <text x={ox - 4} y={y + 4} textAnchor="end" fontSize="9" fill="#aaa">{v}</text>
                  </g>
                );
              })}

              {/* Vertical grid lines */}
              {[0.5, 1.0, 1.5, 2.0].map(r => {
                const x = toX(r, ox);
                return (
                  <g key={r}>
                    <line x1={x} y1={padT} x2={x} y2={oy} stroke={r === 1.0 ? '#e8e8e8' : '#f3f3f3'} strokeWidth={r === 1.0 ? 1.5 : 1} />
                    <text x={x} y={oy + 13} textAnchor="middle" fontSize="9" fill="#aaa">{r}</text>
                  </g>
                );
              })}

              {/* Without DVA (dashed grey) */}
              <polyline
                points={rVals.map((r, i) => `${toX(r, ox)},${toY(ampNoAbs[i], oy)}`).join(' ')}
                fill="none" stroke="#aaa" strokeWidth="2" strokeDasharray="7,4"
              />

              {/* With DVA — draw each continuous segment separately */}
              {segments.map((pts, si) => (
                <polyline key={si} points={pts.join(' ')} fill="none" stroke="#c0392b" strokeWidth="2.5" />
              ))}

              {/* Clip-line indicators at the two new resonance peaks */}
              {segments.length > 1 && (() => {
                const gapX = (
                  parseFloat(segments[0][segments[0].length - 1].split(',')[0]) +
                  parseFloat(segments[1][0].split(',')[0])
                ) / 2;
                return (
                  <g>
                    <line x1={gapX} y1={padT + 2} x2={gapX} y2={oy - 2}
                      stroke="#c0392b" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                    <text x={gapX} y={padT + 14} textAnchor="middle" fontSize="8" fill="#c0392b" opacity="0.7">→∞</text>
                  </g>
                );
              })()}
              {segments.length > 2 && (() => {
                const gapX = (
                  parseFloat(segments[1][segments[1].length - 1].split(',')[0]) +
                  parseFloat(segments[2][0].split(',')[0])
                ) / 2;
                return (
                  <g>
                    <line x1={gapX} y1={padT + 2} x2={gapX} y2={oy - 2}
                      stroke="#c0392b" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                    <text x={gapX} y={padT + 14} textAnchor="middle" fontSize="8" fill="#c0392b" opacity="0.7">→∞</text>
                  </g>
                );
              })()}

              {/* Axes */}
              <line x1={ox} y1={padT - 2} x2={ox} y2={oy + 4} stroke="#888" strokeWidth="1.5" />
              <line x1={ox} y1={oy} x2={ox + plotW + 8} y2={oy} stroke="#888" strokeWidth="1.5" markerEnd="url(#dva1)" />

              {/* Axis labels */}
              <text x={ox + plotW / 2} y={oy + 26} textAnchor="middle" fontSize="10" fill="#555">Frequency Ratio r = ω/ω₁</text>
              <text x={ox - 20} y={padT + plotH / 2} fontSize="9.5" fill="#555"
                transform={`rotate(-90,${ox - 20},${padT + plotH / 2})`} textAnchor="middle">|X₁/X_st|</text>

              {/* Anti-resonance dot and callout */}
              <circle cx={antiX} cy={antiY} r={5} fill="#27ae60" stroke="#fff" strokeWidth="1.5" />
              <line x1={antiX} y1={antiY - 6} x2={antiX + 36} y2={antiY - 32}
                stroke="#27ae60" strokeWidth="1.2" strokeDasharray="3,2" />
              <text x={antiX + 38} y={antiY - 34} fontSize="9.5" fill="#27ae60" fontWeight="700">Anti-resonance</text>
              <text x={antiX + 38} y={antiY - 22} fontSize="9" fill="#27ae60">X₁ ≈ 0 at r = fOpt</text>

              {/* Legend */}
              <line x1={ox + 8} y1={H - 94} x2={ox + 30} y2={H - 94} stroke="#aaa" strokeWidth="2" strokeDasharray="6,3" />
              <text x={ox + 34} y={H - 90} fontSize="9.5" fill="#777">Without DVA</text>
              <line x1={ox + 8} y1={H - 78} x2={ox + 30} y2={H - 78} stroke="#c0392b" strokeWidth="2.5" />
              <text x={ox + 34} y={H - 74} fontSize="9.5" fill="#c0392b">With DVA (μ=0.05, optimal tuning)</text>

              {/* Note box */}
              <rect x={ox} y={H - 64} width={plotW} height={44} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1" />
              <text x={ox + 6} y={H - 51} fontSize="9.5" fill="#7B0D0D" fontWeight="700">DVA creates two new resonances:</text>
              <text x={ox + 6} y={H - 37} fontSize="9.5" fill="#333">One below and one above the original — system must not</text>
              <text x={ox + 6} y={H - 24} fontSize="9.5" fill="#333">operate near these new resonances (use damped DVA)</text>
            </>
          );
        })()}
      </svg>
      <div style={S.dCaption}>The dynamic vibration absorber (DVA), also called a tuned mass damper (TMD), eliminates resonance by attaching a secondary mass-spring system tuned to the primary frequency. At the tuned frequency, the absorber exerts a force exactly equal and opposite to the disturbing force, driving the primary mass amplitude to zero. This creates two new resonances flanking the original — the Den Hartog optimal design minimises the worst-case amplitude across all frequencies by choosing both the tuning ratio and absorber damping optimally.</div>
    </div>
  );
}

// ── Diagram 5: Active Vibration Control Architecture ───────────────
function DiagramActiveControl() {
  const W = 900, H = 310;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Active Vibration Control — Feedforward, Feedback, and Hybrid Architectures</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="ac1" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c0392b" />
          </marker>
          <marker id="ac2" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#2E75B6" />
          </marker>
          <marker id="ac3" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#27ae60" />
          </marker>
        </defs>

        {/* ── Row 1: Feedforward AVC ── */}
        <text x={14} y={22} fontSize="11" fill="#7B0D0D" fontWeight="700">① FEEDFORWARD Active Control  (requires reference signal correlated with disturbance)</text>

        {/* Reference sensor */}
        <rect x={14} y={30} width={80} height={34} rx={4} fill="#fff9e6" stroke="#e07b00" strokeWidth="1.5" />
        <text x={54} y={52} textAnchor="middle" fontSize="10" fill="#7a4800" fontWeight="700">Ref. Sensor</text>
        <line x1={94} y1={47} x2={126} y2={47} stroke="#e07b00" strokeWidth="2" markerEnd="url(#ac1)" />

        {/* Adaptive filter W(z) */}
        <rect x={126} y={30} width={100} height={34} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2" />
        <text x={176} y={48} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">W(z) Filter</text>
        <text x={176} y={61} textAnchor="middle" fontSize="9" fill="#c0392b">FIR / adaptive</text>
        <line x1={226} y1={47} x2={262} y2={47} stroke="#c0392b" strokeWidth="2" markerEnd="url(#ac1)" />

        {/* Actuator */}
        <rect x={262} y={30} width={80} height={34} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5" />
        <text x={302} y={52} textAnchor="middle" fontSize="10" fill="#1a4a7a" fontWeight="700">Actuator</text>
        <line x1={342} y1={47} x2={390} y2={47} stroke="#2E75B6" strokeWidth="2" markerEnd="url(#ac2)" />

        {/* Plant */}
        <rect x={390} y={30} width={80} height={34} rx={4} fill="#fff5f5" stroke="#c0392b" strokeWidth="2" />
        <text x={430} y={52} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Plant</text>

        {/* Disturbance addition */}
        <circle cx={500} cy={47} r={11} fill="#fff" stroke="#888" strokeWidth="1.5" />
        <text x={500} y={51} textAnchor="middle" fontSize="12" fill="#888">Σ</text>
        <line x1={470} y1={47} x2={488} y2={47} stroke="#c0392b" strokeWidth="2" />
        <line x1={500} y1={25} x2={500} y2={36} stroke="#c0392b" strokeWidth="2" markerEnd="url(#ac1)" />
        <text x={500} y={20} textAnchor="middle" fontSize="9.5" fill="#c0392b">Disturbance d</text>

        {/* Error sensor */}
        <line x1={511} y1={47} x2={560} y2={47} stroke="#888" strokeWidth="2" markerEnd="url(#ac1)" />
        <rect x={560} y={30} width={80} height={34} rx={4} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.5" />
        <text x={600} y={52} textAnchor="middle" fontSize="10" fill="#0d4a2a" fontWeight="700">Error Sensor</text>

        {/* Adaptation feedback */}
        <line x1={600} y1={64} x2={600} y2={82} stroke="#27ae60" strokeWidth="1.5" />
        <line x1={600} y1={82} x2={176} y2={82} stroke="#27ae60" strokeWidth="1.5" />
        <line x1={176} y1={82} x2={176} y2={65} stroke="#27ae60" strokeWidth="1.5" markerEnd="url(#ac3)" />
        <text x={388} y={78} textAnchor="middle" fontSize="9.5" fill="#27ae60" fontStyle="italic">LMS adaptation: update W to minimise e²</text>

        {/* Note */}
        <rect x={660} y={28} width={226} height={42} rx={3} fill="#fff9e6" stroke="#e07b00" strokeWidth="1" />
        <text x={666} y={42} fontSize="9.5" fill="#7a4800" fontWeight="700">Best for: tonal noise, periodic disturbances</text>
        <text x={666} y={55} fontSize="9.5" fill="#555">e.g. engine harmonics, fan noise, rotating machinery</text>
        <text x={666} y={67} fontSize="9.5" fill="#555">Algorithm: Filtered-X LMS (FxLMS)</text>

        {/* ── Divider ── */}
        <line x1={10} y1={97} x2={890} y2={97} stroke="#f0cece" strokeWidth="1.5" strokeDasharray="6,4" />

        {/* ── Row 2: Feedback AVC ── */}
        <text x={14} y={112} fontSize="11" fill="#7B0D0D" fontWeight="700">② FEEDBACK Active Control  (requires only error measurement — works for broadband)</text>

        {/* Summing junction */}
        <circle cx={68} cy={145} r={13} fill="#fff" stroke="#c0392b" strokeWidth="2" />
        <text x={68} y={150} textAnchor="middle" fontSize="13" fill="#c0392b" fontWeight="700">Σ</text>
        <text x={14} y={141} fontSize="10" fill="#555">r=0</text>
        <line x1={26} y1={145} x2={54} y2={145} stroke="#c0392b" strokeWidth="2" markerEnd="url(#ac1)" />

        {/* Controller C(s) */}
        <line x1={81} y1={145} x2={116} y2={145} stroke="#c0392b" strokeWidth="2" markerEnd="url(#ac1)" />
        <rect x={116} y={130} width={100} height={30} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2" />
        <text x={166} y={149} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">C(s)</text>

        {/* Actuator */}
        <line x1={216} y1={145} x2={250} y2={145} stroke="#c0392b" strokeWidth="2" markerEnd="url(#ac1)" />
        <rect x={250} y={130} width={80} height={30} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5" />
        <text x={290} y={149} textAnchor="middle" fontSize="10" fill="#1a4a7a" fontWeight="700">Actuator</text>

        <line x1={330} y1={145} x2={368} y2={145} stroke="#2E75B6" strokeWidth="2" markerEnd="url(#ac2)" />

        {/* Plant */}
        <rect x={368} y={130} width={80} height={30} rx={4} fill="#fff5f5" stroke="#c0392b" strokeWidth="2" />
        <text x={408} y={149} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Plant</text>

        {/* Sensor */}
        <line x1={448} y1={145} x2={520} y2={145} stroke="#888" strokeWidth="2" markerEnd="url(#ac1)" />
        <rect x={520} y={130} width={80} height={30} rx={4} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.5" />
        <text x={560} y={149} textAnchor="middle" fontSize="10" fill="#0d4a2a" fontWeight="700">Sensor</text>

        {/* Feedback path */}
        <line x1={560} y1={160} x2={560} y2={178} stroke="#27ae60" strokeWidth="2" />
        <line x1={560} y1={178} x2={68} y2={178} stroke="#27ae60" strokeWidth="2" />
        <line x1={68} y1={178} x2={68} y2={159} stroke="#27ae60" strokeWidth="2" markerEnd="url(#ac3)" />
        <text x={68} y={158} textAnchor="middle" fontSize="10" fill="#c0392b">−</text>
        <text x={314} y={194} textAnchor="middle" fontSize="9.5" fill="#27ae60" fontStyle="italic">Output feedback — closed loop</text>

        {/* Feedback note */}
        <rect x={660} y={128} width={226} height={46} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1" />
        <text x={666} y={142} fontSize="9.5" fill="#7B0D0D" fontWeight="700">Best for: broadband, unknown disturbance</text>
        <text x={666} y={155} fontSize="9.5" fill="#555">Sky-hook damping, PPF, velocity feedback</text>
        <text x={666} y={167} fontSize="9.5" fill="#555">Limited by stability (Bode integral constraint)</text>

        {/* ── Row 3: Comparison ── */}
        <line x1={10} y1={198} x2={890} y2={198} stroke="#f0cece" strokeWidth="1.5" strokeDasharray="6,4" />
        <text x={14} y={214} fontSize="11" fill="#7B0D0D" fontWeight="700">③ Key AVC Technologies</text>

        {[
          { x: 14,  col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b',  title: 'Sky-hook Damping',   lines: ['F_act = −c_sky · ẋ_abs', 'Virtual damper to inertial ref.', 'Improves ζ without degrading isolation'] },
          { x: 222, col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6',  title: 'Positive Position\nFeedback (PPF)',  lines: ['Second-order compensator', 'Adds damping to flexible modes', 'Guaranteed stable for collocated'] },
          { x: 430, col: '#27ae60', bg: '#f0fff4', bc: '#27ae60',  title: 'Inertial Actuator\n(Proof Mass)',    lines: ['Reaction mass on spring-voice coil', 'Force output ∝ current × position', 'Used in floors, vehicle seats'] },
          { x: 638, col: '#e07b00', bg: '#fff9e6', bc: '#e07b00',  title: 'Magnetorheological\n(MR) Damper',   lines: ['Viscosity controlled by B field', 'Semi-active: fail-safe to passive', 'Bridges active and passive'] },
        ].map(({ x, col, bg, bc, title, lines }) => (
          <g key={x}>
            <rect x={x} y={220} width={200} height={80} rx={4} fill={bg} stroke={bc} strokeWidth="1.5" />
            <text x={x + 6} y={235} fontSize="10.5" fill={col} fontWeight="700">{title.replace('\n', ' ')}</text>
            {lines.map((l, i) => (
              <text key={i} x={x + 6} y={250 + i * 15} fontSize="9.5" fill="#333">{l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Active vibration control (AVC) uses sensors, a control algorithm, and actuators to generate a secondary force that cancels or attenuates unwanted vibration. Feedforward control requires a reference signal correlated with the disturbance and uses adaptive filtering (FxLMS) to track time-varying systems. Feedback control operates on the error signal only and is suitable for broadband rejection but is limited by the Bode integral: attenuation at one frequency must be compensated by amplification at another. Semi-active systems (MR dampers) vary their damping coefficient in real time, achieving near-active performance with passive fail-safe behaviour.</div>
    </div>
  );
}

// ── Diagram 6: Isolation Platform — Multi-DOF ─────────────────────
function DiagramMultiDOF() {
  const W = 900, H = 310;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Multi-DOF Isolation Platform — 6-DOF Rigid-Body Modes and Coupling</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="md1" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c0392b" />
          </marker>
        </defs>

        {/* ── Platform drawing ── */}
        {(() => {
          const px = 40, py = 80, pw = 260, ph = 80;
          const isoPositions = [[px + 30, py + ph], [px + pw / 2, py + ph], [px + pw - 30, py + ph],
                                [px + 30, py + ph], [px + pw - 30, py + ph]];
          const floorY = py + ph + 80;
          return (
            <g>
              {/* Platform */}
              <rect x={px} y={py} width={pw} height={ph} rx={6} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2.5" />
              <text x={px + pw / 2} y={py + 30} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Payload Platform</text>
              <text x={px + pw / 2} y={py + 48} textAnchor="middle" fontSize="10.5" fill="#555">Mass M, Inertias I_x, I_y, I_z</text>
              <text x={px + pw / 2} y={py + 64} textAnchor="middle" fontSize="10" fill="#555">Centre of mass (CoM)</text>

              {/* CoM dot */}
              <circle cx={px + pw / 2} cy={py + 54} r={5} fill="#c0392b" />

              {/* Isolators (5 shown — 3 corners + 2 side for 6-DOF) */}
              {[[px + 20, py + ph], [px + pw - 20, py + ph], [px + pw / 2, py + ph]].map(([ix, iy], i) => {
                const springPts = [];
                const x0 = ix, y0 = iy, y1 = floorY;
                springPts.push(`${x0},${y0}`);
                for (let j = 0; j < 5; j++) {
                  const yy = y0 + (y1 - y0) * (j + 0.3) / 5;
                  springPts.push(`${x0 + (j % 2 === 0 ? 8 : -8)},${yy}`);
                }
                springPts.push(`${x0},${y1}`);
                return (
                  <g key={i}>
                    <polyline points={springPts.join(' ')} fill="none" stroke="#2E75B6" strokeWidth="2" />
                    {/* Damper parallel */}
                    <line x1={x0 + 14} y1={iy} x2={x0 + 14} y2={(iy + floorY) / 2 - 6} stroke="#e07b00" strokeWidth="2" />
                    <rect x={x0 + 8} y={(iy + floorY) / 2 - 6} width={12} height={12} fill="#fff9e6" stroke="#e07b00" strokeWidth="1.5" />
                    <line x1={x0 + 14} y1={(iy + floorY) / 2 + 6} x2={x0 + 14} y2={floorY} stroke="#e07b00" strokeWidth="2" />
                  </g>
                );
              })}

              {/* Floor */}
              <rect x={px - 10} y={floorY} width={pw + 20} height={12} rx={2} fill="#999" />

              {/* 6 DOF arrows */}
              {[
                { x: px + pw + 10, y: py + 40, dx: 30, dy: 0,  label: 'x (surge)',  col: '#c0392b' },
                { x: px + pw / 2,  y: py - 16, dx: 0,  dy: -28, label: 'z (heave)', col: '#c0392b' },
                { x: px + pw + 10, y: py + 68, dx: 30, dy: 0,  label: 'y (sway)',   col: '#2E75B6' },
              ].map(({ x, y, dx, dy, label, col }) => (
                <g key={label}>
                  <line x1={x} y1={y} x2={x + dx} y2={y + dy} stroke={col} strokeWidth="2" markerEnd="url(#md1)" />
                  <text x={x + dx + 4} y={y + dy / 2 + 4} fontSize="9.5" fill={col} fontWeight="700">{label}</text>
                </g>
              ))}
              {/* Rotational arrows (arcs) */}
              <path d="M 155 68 A 22 22 0 0 1 195 68" fill="none" stroke="#27ae60" strokeWidth="2" markerEnd="url(#md1)" />
              <text x={175} y={60} textAnchor="middle" fontSize="9.5" fill="#27ae60" fontWeight="700">θz (yaw)</text>
              <path d="M 40 130 A 18 18 0 0 0 40 166" fill="none" stroke="#9b59b6" strokeWidth="2" markerEnd="url(#md1)" />
              <text x={24} y={148} fontSize="9.5" fill="#9b59b6" fontWeight="700">θx</text>
            </g>
          );
        })()}

        {/* Right: 6-DOF modes table */}
        <rect x={330} y={10} width={558} height={H - 18} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={609} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Six Rigid-Body Modes and Design Principles</text>

        {[
          { y: 36,  col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b',
            title: 'Translational Modes (3)',
            lines: ['Heave (z): most critical for vertical isolation', 'Surge (x) and Sway (y): horizontal modes', 'fₙ(vertical) = (1/2π)√(k_v/M)'] },
          { y: 106, col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6',
            title: 'Rotational Modes (3)',
            lines: ['Roll (θx), Pitch (θy), Yaw (θz)', 'fₙ(roll) = (1/2π)√(k_v·L²/I_x)', 'Couple with translations if CoM ≠ elastic centre'] },
          { y: 176, col: '#27ae60', bg: '#f0fff4', bc: '#27ae60',
            title: 'Decoupling Conditions',
            lines: ['Mount elastic centre (shear centre) must coincide with CoM', 'Otherwise translation ↔ rotation coupling occurs', 'Decoupled: 6 independent SDOF mode equations'] },
          { y: 246, col: '#e07b00', bg: '#fff9e6', bc: '#e07b00',
            title: 'Isolator Placement Rules',
            lines: ['Symmetry: isolators symmetric about CoM → no coupling', 'Height: raise/lower mounts to adjust rock frequency', 'Corner mounts: stiffer in vertical, softer in horizontal'] },
        ].map(({ y, col, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={336} y={y} width={546} height={lines.length * 17 + 22} rx={3} fill={bg} stroke={bc} strokeWidth="1.2" />
            <text x={342} y={y + 15} fontSize="11" fill={col} fontWeight="700">{title}</text>
            {lines.map((line, i) => (
              <text key={i} x={342} y={y + 30 + i * 17} fontSize="9.5" fill="#333">{line}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>A real isolation platform has six rigid-body degrees of freedom: three translational (heave, surge, sway) and three rotational (roll, pitch, yaw). All six can experience resonances that must be designed to lie well below the disturbing frequency. Cross-coupling between translational and rotational modes occurs whenever the centre of mass does not coincide with the elastic centre of the mount system. Proper isolator placement and stiffness distribution achieves decoupling, allowing each of the 6 modes to be treated as an independent SDOF system.</div>
    </div>
  );
}

// ── Diagram 7: Damping Treatments — CLD and Free Layer ────────────
function DiagramDampingTreatments() {
  const W = 900, H = 300;
  const PW = (W - 30) / 2;

  // Loss factor comparison bar chart
  const materials = [
    { name: 'Steel (bare)', eta: 0.001, col: '#999' },
    { name: 'Steel + free\nCLD layer', eta: 0.02,  col: '#e07b00' },
    { name: 'Steel + constrained\nCLD layer', eta: 0.15, col: '#c0392b' },
    { name: 'Viscoelastic\nmount', eta: 0.3,  col: '#2E75B6' },
    { name: 'Acoustic foam\npanel', eta: 0.1,  col: '#27ae60' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Damping Treatments — Free Layer, Constrained Layer, and Loss Factor Comparison</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="dt1" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#888" />
          </marker>
        </defs>

        {/* Panel 1: CLD diagrams */}
        <rect x={10} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={10 + PW / 2} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Damping Treatment Cross-Sections</text>

        {/* Free layer damping */}
        <text x={24} y={52} fontSize="11" fill="#e07b00" fontWeight="700">A. Free-Layer (Extension Damping)</text>
        {/* Base */}
        <rect x={24} y={58} width={200} height={22} rx={2} fill="#ccc" stroke="#888" strokeWidth="1.5" />
        <text x={124} y={73} textAnchor="middle" fontSize="10" fill="#555">Base structure</text>
        {/* Damping layer */}
        <rect x={24} y={36} width={200} height={22} rx={2} fill="#fff9e6" stroke="#e07b00" strokeWidth="2" />
        <text x={124} y={51} textAnchor="middle" fontSize="10" fill="#7a4800" fontWeight="700">Viscoelastic layer (free)</text>
        {/* Bending arrows */}
        <path d="M 50 58 Q 80 44 110 58 Q 140 72 170 58 Q 200 44 224 58" fill="none" stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,2" />
        <text x={124} y={100} textAnchor="middle" fontSize="9.5" fill="#555">Extension/compression in layer → energy dissipation</text>
        <text x={124} y={113} textAnchor="middle" fontSize="9.5" fill="#e07b00">η_eff ≈ 3η_v·(E_v/E_b)·(h_v/h_b)</text>

        {/* Constrained layer damping */}
        <text x={24} y={136} fontSize="11" fill="#c0392b" fontWeight="700">B. Constrained-Layer (Shear Damping)</text>
        {/* Base */}
        <rect x={24} y={142} width={200} height={22} rx={2} fill="#ccc" stroke="#888" strokeWidth="1.5" />
        <text x={124} y={157} textAnchor="middle" fontSize="10" fill="#555">Base structure</text>
        {/* Viscoelastic */}
        <rect x={24} y={122} width={200} height={20} rx={2} fill="#fff9e6" stroke="#e07b00" strokeWidth="2" />
        <text x={124} y={136} textAnchor="middle" fontSize="10" fill="#7a4800" fontWeight="700">Viscoelastic (shear layer)</text>
        {/* Constraining layer */}
        <rect x={24} y={104} width={200} height={18} rx={2} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2" />
        <text x={124} y={117} textAnchor="middle" fontSize="10" fill="#7B0D0D" fontWeight="700">Constraining layer (stiff)</text>
        {/* Shear arrows */}
        {[40, 80, 120, 160, 200].map(x => (
          <g key={x}>
            <line x1={x} y1={120} x2={x + 8} y2={142} stroke="#c0392b" strokeWidth="1" strokeDasharray="2,2" />
          </g>
        ))}
        <text x={124} y={180} textAnchor="middle" fontSize="9.5" fill="#555">SHEAR in viscoelastic layer → much higher η_eff</text>
        <text x={124} y={193} textAnchor="middle" fontSize="9.5" fill="#c0392b">Optimal: maximise shear strain in VE layer</text>

        {/* Add-on dampers */}
        <text x={24} y={218} fontSize="11" fill="#2E75B6" fontWeight="700">C. Tuned Damper / DVA attached to panel</text>
        <rect x={24} y={224} width={200} height={16} rx={2} fill="#ccc" stroke="#888" strokeWidth="1.5" />
        <rect x={90} y={208} width={68} height={16} rx={2} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="2" />
        <line x1={124} y1={224} x2={124} y2={208} stroke="#2E75B6" strokeWidth="2" />
        <text x={124} y={258} textAnchor="middle" fontSize="9.5" fill="#2E75B6">DVA: m_a ≈ 1–5% of panel modal mass</text>
        <text x={124} y={272} textAnchor="middle" fontSize="9.5" fill="#555">Targets one specific mode — very effective</text>

        {/* Panel 2: Loss factor bar chart */}
        <rect x={PW + 20} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={PW + 20 + PW / 2} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Loss Factor η Comparison (log scale)</text>

        {(() => {
          const ox = PW + 36, oy = H - 34, ow = PW - 46, maxEta = 0.35;
          const barH = 34, gap = 8;
          return (
            <g>
              {/* Axis */}
              <line x1={ox} y1={oy} x2={ox + ow} y2={oy} stroke="#888" strokeWidth="1.5" markerEnd="url(#dt1)" />
              <text x={ox + ow + 4} y={oy + 4} fontSize="9" fill="#888">η</text>
              {[0, 0.1, 0.2, 0.3].map(v => {
                const x = ox + v / maxEta * ow;
                return (
                  <g key={v}>
                    <line x1={x} y1={oy} x2={x} y2={oy + 4} stroke="#bbb" strokeWidth="1" />
                    <text x={x} y={oy + 14} textAnchor="middle" fontSize="9" fill="#aaa">{v}</text>
                  </g>
                );
              })}

              {materials.map(({ name, eta, col }, i) => {
                const y = 42 + i * (barH + gap);
                const barW = eta / maxEta * ow;
                return (
                  <g key={i}>
                    <rect x={ox} y={y} width={barW} height={barH - 6} rx={3} fill={col} opacity="0.85" />
                    {name.split('\n').map((line, li) => (
                      <text key={li} x={ox - 4} y={y + 10 + li * 12} textAnchor="end" fontSize="9.5" fill={col} fontWeight={li === 0 ? '700' : '400'}>{line}</text>
                    ))}
                    <text x={ox + barW + 4} y={y + 14} fontSize="9.5" fill={col} fontWeight="700">η={eta}</text>
                  </g>
                );
              })}

              {/* Note */}
              <rect x={ox} y={H - 54} width={ow} height={18} rx={2} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1" />
              <text x={ox + 4} y={H - 42} fontSize="9.5" fill="#7B0D0D" fontWeight="700">Higher η → faster energy decay → lower resonance peaks → less structure-borne noise</text>
            </g>
          );
        })()}
      </svg>
      <div style={S.dCaption}>Damping treatments add energy dissipation directly to the structure. Free-layer treatments apply a viscoelastic layer that undergoes extension and compression during bending, dissipating energy with an effective loss factor proportional to the stiffness and thickness ratio. Constrained-layer damping (CLD) sandwiches the viscoelastic material between the base and a stiff constraining layer — bending forces the VE layer into shear, which is far more effective. The loss factor η directly sets the resonance peak height: |H|_peak ≈ 1/η.</div>
    </div>
  );
}

// ── Diagram 8: Comparison — Passive vs Semi-Active vs Active ──────
function DiagramStrategyComparison() {
  const W = 900, H = 270;
  const strategies = [
    {
      name: 'Passive',
      col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6',
      props: [
        ['Complexity', '★☆☆☆'],
        ['Cost', '★☆☆☆'],
        ['Performance', '★★★☆'],
        ['Power needed', 'None'],
        ['Fail-safe', 'Yes (inherent)'],
        ['Best f range', 'f > √2 fₙ'],
        ['Limitation', 'Trade-off: damping vs. isolation'],
      ],
      examples: 'Rubber mounts, springs, DVA',
    },
    {
      name: 'Semi-Active',
      col: '#e07b00', bg: '#fff9e6', bc: '#e07b00',
      props: [
        ['Complexity', '★★☆☆'],
        ['Cost', '★★☆☆'],
        ['Performance', '★★★★'],
        ['Power needed', 'Low (valve/coil)'],
        ['Fail-safe', 'Yes (passive mode)'],
        ['Best f range', 'Broad (adaptive ωₙ)'],
        ['Limitation', 'Cannot add energy to system'],
      ],
      examples: 'MR dampers, ER fluids, variable stiffness',
    },
    {
      name: 'Active',
      col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b',
      props: [
        ['Complexity', '★★★★'],
        ['Cost', '★★★★'],
        ['Performance', '★★★★★'],
        ['Power needed', 'High (actuator)'],
        ['Fail-safe', 'No (may destabilise)'],
        ['Best f range', 'Low and broadband'],
        ['Limitation', 'Stability, cost, power, weight'],
      ],
      examples: 'Voice coil, piezo stack, hydraulic',
    },
  ];

  const cardW = (W - 20) / 3;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Strategy Comparison — Passive, Semi-Active, and Active Vibration Control</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        {strategies.map(({ name, col, bg, bc, props, examples }, i) => {
          const x = 8 + i * (cardW + 3), y = 8;
          return (
            <g key={i}>
              <rect x={x} y={y} width={cardW} height={H - 20} rx={5} fill={bg} stroke={bc} strokeWidth="2" />
              <rect x={x} y={y} width={cardW} height={30} rx={4} fill={bc} />
              <text x={x + cardW / 2} y={y + 20} textAnchor="middle" fontSize="14" fill="#fff" fontWeight="700">{name}</text>
              {props.map(([key, val], pi) => (
                <g key={pi}>
                  <rect x={x + 6} y={y + 36 + pi * 26} width={cardW - 12} height={23} rx={2}
                    fill={pi % 2 === 0 ? 'rgba(255,255,255,0.6)' : 'transparent'} />
                  <text x={x + 12} y={y + 51 + pi * 26} fontSize="10" fill={col} fontWeight="700">{key}</text>
                  <text x={x + cardW - 10} y={y + 51 + pi * 26} textAnchor="end" fontSize="10" fill="#333">{val}</text>
                </g>
              ))}
              <rect x={x + 6} y={y + H - 52} width={cardW - 12} height={30} rx={3} fill={bc} opacity="0.12" />
              <text x={x + 12} y={y + H - 39} fontSize="9.5" fill={col} fontWeight="700">Examples:</text>
              <text x={x + 12} y={y + H - 27} fontSize="9.5" fill="#333">{examples}</text>
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>The three control strategies occupy different positions on the cost-performance-complexity spectrum. Passive systems are reliable and fail-safe but cannot adapt. Semi-active systems (primarily MR and ER dampers) adjust their dissipation coefficient in real time, approximating active performance with passive fail-safe characteristics and modest power consumption. Active systems achieve the highest performance — capable of both isolation and absorption, broadband and narrowband — but require significant power, introduce instability risk if the controller fails, and are expensive. For most industrial applications, passive or semi-active approaches are preferred unless the performance requirements truly demand active control.</div>
    </div>
  );
}

// ── Diagram 9: Practical Design Flowchart ─────────────────────────
function DiagramDesignProcess() {
  const W = 900, H = 290;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Isolation & Control Design Process — Step-by-Step Flowchart</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="dp1" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c0392b" />
          </marker>
          <marker id="dp2" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#2E75B6" />
          </marker>
        </defs>

        {/* Steps */}
        {[
          { x: 10,  y: 20,  w: 130, h: 50, col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b',
            num: '①', title: 'Characterise\nDisturbance', sub: 'Freq, amplitude,\ndirections, spectrum' },
          { x: 170, y: 20,  w: 130, h: 50, col: '#e07b00', bg: '#fff9e6', bc: '#e07b00',
            num: '②', title: 'Define\nRequirements', sub: 'T_target at each f,\nstatic deflection limits' },
          { x: 330, y: 20,  w: 130, h: 50, col: '#27ae60', bg: '#f0fff4', bc: '#27ae60',
            num: '③', title: 'Choose\nωₙ Target', sub: 'ωₙ ≤ ω_min/√2\n(r > √2 at all freqs)' },
          { x: 490, y: 20,  w: 130, h: 50, col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6',
            num: '④', title: 'Select Isolator\nType & Damping', sub: 'k = Mωₙ²; choose ζ\nto limit resonance' },
          { x: 650, y: 20,  w: 130, h: 50, col: '#9b59b6', bg: '#f8f0ff', bc: '#9b59b6',
            num: '⑤', title: 'Verify Multi-\nDOF Modes', sub: '6 DOF check;\ncoupling conditions' },
          { x: 790, y: 20,  w: 102, h: 50, col: '#c0392b', bg: '#fff5f5', bc: '#c0392b',
            num: '⑥', title: 'Test &\nValidate', sub: 'Measure T; compare\nto prediction; iterate' },
        ].map(({ x, y, w, h, col, bg, bc, num, title, sub }) => (
          <g key={x}>
            <rect x={x} y={y} width={w} height={h} rx={5} fill={bg} stroke={bc} strokeWidth="2" />
            <text x={x + 14} y={y + 16} fontSize="14" fill={col} fontWeight="700">{num}</text>
            {title.split('\n').map((t, i) => (
              <text key={i} x={x + 28} y={y + 14 + i * 14} fontSize="10.5" fill={col} fontWeight="700">{t}</text>
            ))}
            {sub.split('\n').map((s, i) => (
              <text key={i} x={x + 6} y={y + h - 18 + i * 12} fontSize="9" fill="#555">{s}</text>
            ))}
          </g>
        ))}

        {/* Arrows between steps */}
        {[140, 300, 460, 620, 780].map(x => (
          <line key={x} x1={x} y1={45} x2={x + 28} y2={45} stroke="#c0392b" strokeWidth="2" markerEnd="url(#dp1)" />
        ))}

        {/* Design decision diamond */}
        {(() => {
          const dx = 450, dy = 100;
          return (
            <g>
              <polygon points={`${dx},${dy} ${dx+60},${dy+28} ${dx},${dy+56} ${dx-60},${dy+28}`}
                fill="#FEF0F0" stroke="#c0392b" strokeWidth="2" />
              <text x={dx} y={dy + 24} textAnchor="middle" fontSize="10" fill="#7B0D0D" fontWeight="700">T meets</text>
              <text x={dx} y={dy + 38} textAnchor="middle" fontSize="10" fill="#7B0D0D" fontWeight="700">requirement?</text>
              {/* Yes */}
              <line x1={dx + 60} y1={dy + 28} x2={dx + 110} y2={dy + 28} stroke="#27ae60" strokeWidth="2" markerEnd="url(#dp1)" />
              <text x={dx + 80} y={dy + 22} fontSize="10" fill="#27ae60" fontWeight="700">YES</text>
              <rect x={dx + 110} y={dy + 14} width={80} height={28} rx={4} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.5" />
              <text x={dx + 150} y={dy + 32} textAnchor="middle" fontSize="10" fill="#27ae60" fontWeight="700">DONE ✓</text>
              {/* No — add DVA or active */}
              <line x1={dx} y1={dy + 56} x2={dx} y2={dy + 90} stroke="#c0392b" strokeWidth="2" markerEnd="url(#dp1)" />
              <text x={dx + 6} y={dy + 76} fontSize="10" fill="#c0392b" fontWeight="700">NO</text>
              <rect x={dx - 100} y={dy + 90} width={200} height={50} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2" />
              <text x={dx} y={dy + 112} textAnchor="middle" fontSize="10.5" fill="#7B0D0D" fontWeight="700">Add DVA / CLD / Active AVC</text>
              <text x={dx} y={dy + 128} textAnchor="middle" fontSize="9.5" fill="#555">Re-evaluate transmissibility and stability</text>
            </g>
          );
        })()}

        {/* Key principles row */}
        <rect x={10} y={226} width={868} height={56} rx={4} fill="#fff" stroke="#f0cece" />
        <text x={450} y={242} textAnchor="middle" fontSize="10.5" fill="#7B0D0D" fontWeight="700">Design Rules of Thumb</text>
        {[
          { x: 20,  lines: ['ωₙ target:', '≤ f_disturb / 3'] },
          { x: 155, lines: ['Static deflect.:', 'δ_st = g/ωₙ²'] },
          { x: 295, lines: ['Resonance peak:', 'T_peak = 1/(2ζ)'] },
          { x: 440, lines: ['High-f slope:', '−40 dB/dec (undamped)'] },
          { x: 600, lines: ['DVA mass ratio:', 'μ = 0.02–0.1 typical'] },
          { x: 750, lines: ['CLD coverage:', '≥ 50% panel area'] },
        ].map(({ x, lines }) => (
          <g key={x}>
            {lines.map((l, i) => (
              <text key={i} x={x} y={256 + i * 14} fontSize="9.5" fill={i === 0 ? '#c0392b' : '#555'} fontWeight={i === 0 ? '700' : '400'}>{l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Vibration isolation design follows a structured process: characterise the disturbance spectrum, define the transmissibility target, set the isolator natural frequency well below the disturbing frequency, select the appropriate hardware, verify the multi-DOF mode arrangement, and test. If requirements are not met, add a DVA for narrow-band problems, CLD for structural resonances, or active control for broadband or very low frequency challenges. Iteration is normal — the design loop typically closes in 2–3 cycles.</div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────
export default function VibrationControl() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Vibration Control &amp; Isolation</h1>
      <p style={S.p}>
        Vibration control encompasses every technique used to reduce unwanted oscillations in mechanical systems — from the rubber mounts beneath a washing machine to the active isolation platforms that protect electron microscopes from floor vibration. The field divides naturally into three complementary strategies: passive isolation, which uses compliant elements to decouple the structure from its vibration source; dynamic absorption, which attaches a secondary oscillator tuned to cancel a specific frequency; and active control, which uses sensors, algorithms, and actuators to generate cancelling forces in real time. Understanding the fundamental transmissibility relationship and its trade-offs is the gateway to all of vibration control.
      </p>

      <InfoBox title="Core Ideas at a Glance" items={[
        "Transmissibility T(r,ζ) = √[(1+(2ζr)²)/((1−r²)²+(2ζr)²)]: for r > √2, the isolator attenuates; for r < √2, it amplifies",
        "The fundamental isolation trade-off: high damping controls resonance but degrades high-frequency isolation (T → 1 as ζ→∞)",
        "Isolator natural frequency: ωₙ = √(k/m); design target ωₙ ≤ ω_disturb/3 for good isolation margin",
        "Dynamic Vibration Absorber (DVA): secondary mass-spring tuned to primary resonance drives primary amplitude to zero at that frequency",
        "Den Hartog optimal DVA tuning: f_opt = 1/(1+μ), ζ_opt = √(3μ/[8(1+μ)³]), where μ = m_absorber/M_primary",
        "Constrained-layer damping (CLD) shears the viscoelastic layer during bending — far more effective than free-layer treatment",
        "Active vibration control (AVC): feedforward for tonal/periodic disturbances (FxLMS); feedback for broadband (sky-hook, PPF)",
        "Semi-active (MR dampers): vary dissipation coefficient in real time — passive fail-safe with near-active performance",
        "6-DOF isolation: all six rigid-body modes must have natural frequencies below the disturbing frequency; CoM–elastic-centre alignment prevents mode coupling",
        "Bode integral constraint: feedback cannot reduce transmissibility everywhere — attenuation at one frequency requires amplification at another",
      ]} />

      <h2 style={S.h2}>Transmissibility — The Fundamental Relationship</h2>
      <p style={S.p}>
        Transmissibility is the ratio of the amplitude of the output (force transmitted to the foundation, or motion of the payload) to the amplitude of the input (machine force, or base motion). It is the single most important design quantity in vibration isolation, and understanding its dependence on the frequency ratio r = ω/ωₙ and damping ratio ζ is essential.
      </p>
      <DiagramTransmissibility />
      <Table2 rows={[
        ["Transmissibility formula", "T = √[(1+(2ζr)²) / ((1−r²)²+(2ζr)²)]\nr = ω/ωₙ (frequency ratio);  ζ = c/(2√(km)) (damping ratio)\nValid for both force and base-motion isolation — same expression"],
        ["Three regions", "r < 1: below resonance — T ≈ 1, slight amplification\nr = 1: resonance — T = 1/(2ζ), peak amplification\nr > √2: isolation region — T < 1 and decreasing\nr ≫ 1: T ≈ 1/r² (undamped); T ≈ 2ζ/r (highly damped — limited benefit)"],
        ["High-frequency asymptote", "Undamped (ζ→0): T ≈ 1/r² → slope of −40 dB/decade\nDamped: T → 2ζ/r at high r → slope of only −20 dB/decade\nConclusion: high damping degrades high-frequency isolation — fundamental trade-off"],
        ["Isolation design rule", "For 10× attenuation (T=0.1): need r ≈ 3.3 (undamped)\nFor 100× attenuation (T=0.01): need r ≈ 10\nWorking formula: fₙ ≤ f_disturb / (√2 · √(1/T_target) · correction)"],
        ["Static deflection", "δ_st = Mg/k = g/ωₙ²\nδ_st = 25 mm → fₙ ≈ 3.1 Hz (good for 10+ Hz disturb.)\nδ_st = 100 mm → fₙ ≈ 1.6 Hz (excellent low-freq isolation)\nAir springs can achieve δ_st > 1 m equivalent"],
        ["Damping selection", "ζ = 0.05–0.1: low damping → excellent isolation, large resonance peak\nζ = 0.15–0.3: compromise — controlled resonance, modest isolation loss\nζ > 0.5: over-damped — resonance controlled but poor isolation above 2fₙ\nSolution: passive isolator with auxiliary viscous damper in parallel"],
        ["Two-stage isolation", "Series combination of two isolator stages\nT_total = T₁ × T₂ (transmissibilities multiply)\nCan achieve T ∝ 1/r⁴ slope (−80 dB/decade) in isolation region\nPractical constraint: intermediate mass must not resonate within operating band"],
      ]} />

      <h2 style={S.h2}>Isolator Physical Models</h2>
      <p style={S.p}>
        Two canonical physical configurations cover the majority of real isolation problems: a machine mounted on isolators to reduce the force it transmits to a building structure (force isolation), and sensitive equipment mounted on isolators to protect it from a vibrating floor (motion isolation). Both are governed by the same transmissibility function.
      </p>
      <DiagramIsolatorModel />
      <Table2 rows={[
        ["SDOF isolator equations", "mẍ + c(ẋ−ẏ) + k(x−y) = 0  (base excitation)\nmẍ + cẋ + kx = F₀sinωt  (force excitation)\nIn both cases: T = |X(ω)|/|Y(ω)| or |Fₜ|/|F₀| = same formula"],
        ["Isolator stiffness", "k = mωₙ²;  choose ωₙ to achieve required frequency ratio\nk must also support static load without excessive sag\nSoft isolator (low k) → low ωₙ → good isolation → large static deflection"],
        ["Viscous damping coefficient", "c = 2ζ√(km) = 2ζmωₙ\nOften provided inherently by rubber (loss factor η ≈ 2ζ)\nExternal dashpot parallel to spring provides additional c"],
        ["Stiffness asymmetry", "Vertical stiffness k_v = Mg/δ_st (set by static load)\nHorizontal stiffness k_h often 0.3–0.6 × k_v for elastomeric mounts\nDesign both: horizontal modes often at lower frequency than vertical"],
        ["Impedance mismatch", "Effective isolation also requires impedance mismatch at the interface\nHigh-impedance machine → low-impedance mount → low-impedance foundation\nMismatch factor: T ∝ Z_mount/Z_source at mid-frequencies"],
        ["Environmental effects", "Temperature: rubber stiffness increases 2–3× from +20°C to −40°C → shifts fₙ\nCreep: under constant load, elastomers soften over weeks–months\nFatigue: cyclic loading limits elastomeric mount lifetime\nCheck manufacturer data for operating temperature range and service life"],
      ]} />

      <h2 style={S.h2}>Isolator Technologies</h2>
      <p style={S.p}>
        The choice of isolator technology depends on the required natural frequency, load capacity, environmental conditions, and available space. Each technology occupies a different region of the stiffness-damping parameter space.
      </p>
      <DiagramIsolatorTypes />
      <Table2 rows={[
        ["Elastomeric mounts", "Natural rubber, neoprene, or silicone sandwiched between metal plates\nLoss factor η ≈ 0.04–0.15 (self-damped, no external dashpot needed)\nTypical fₙ: 5–30 Hz; stiffness ratio k_h/k_v ≈ 0.3–0.8\nAnti-vibration mounts (AVM): standard catalogue item, easy to install"],
        ["Helical coil springs", "Very low natural frequency: fₙ 1–8 Hz possible\nMinimal inherent damping (η ≈ 0.005) — always combine with damper\nHigh static deflection (25–150 mm) → requires levelling or travel stops\nUsed under precision floors, printing presses, forging hammers"],
        ["Air springs (pneumatics)", "Bellows or sleeve containing pressurised air; k = p·A²/V (pressure, area, volume)\nLowest available fₙ: 0.5–3 Hz — best passive isolation technology\nHeight can be controlled by adding/removing air — useful for levelling\nUsed in optical tables, vehicle suspensions, precision metrology frames"],
        ["Wire rope isolators", "Stainless steel wire rope wound into helical coils\nHighly nonlinear: stiffening at large amplitude, softening initially\nExcellent damping (η ≈ 0.1–0.2) and wide temperature range (−60 to +150°C)\nIdeal for shock and vibration in harsh environments (military, marine)"],
        ["Magnetorheological (MR) dampers", "Ferrous particles suspended in oil; yield stress controlled by electromagnet\nResponse time: < 10 ms; force range: 0 to several kN\nFails open to passive state (safe); controllable via 0–2 A current\nWidely used in vehicle suspensions, civil engineering dampers"],
        ["Active isolation platforms", "Accelerometers + voice coil or piezoelectric actuators\nAchieves fₙ_eff < 0.1 Hz — attenuates floor vibration above ~1 Hz\nUsed in semiconductor lithography (< nm vibration budget), electron microscopes\nRequires power, control electronics, and stability margin management"],
      ]} />

      <h2 style={S.h2}>Dynamic Vibration Absorber</h2>
      <p style={S.p}>
        The dynamic vibration absorber (DVA), also known as a tuned mass damper (TMD), is an elegant passive solution for narrow-band or tonal vibration problems. Rather than isolating the machine from the foundation, it attaches a secondary oscillator directly to the primary structure that, when properly tuned, creates a cancelling force at the target frequency.
      </p>
      <DiagramDVA />
      <Table2 rows={[
        ["Operating principle", "At ω = ω_absorber: absorber displacement x₂ = F/(k₂) out of phase with primary\nForce from absorber spring on primary: −k₂·(x₁−x₂) = +k₂·x₂ ≈ F₀ → cancels excitation\nResult: primary mass X₁ = 0 at the exact tuned frequency"],
        ["Undamped DVA behaviour", "Creates two new resonances flanking the original natural frequency ω₁\nAnti-resonance (X₁=0) between the two new peaks\nFrequency of two new peaks: ω²/ω₁² = 1+μ/2 ± √(μ+μ²/4)"],
        ["Den Hartog optimal tuning", "For damped DVA: minimise worst-case |X₁/X_st| over all frequencies\nOptimal tuning ratio: f_opt = ω₂/ω₁ = 1/(1+μ)\nOptimal damping: ζ_opt = √(3μ/[8(1+μ)³])\nResulting peak: |X₁/X_st|_max = 1 + √(2/μ) (at the two fixed points)"],
        ["Mass ratio μ selection", "μ = m_absorber/M_primary (modal mass)\nLarger μ → lower peak amplitude, wider effective bandwidth\nTypical μ = 0.02–0.10 (2–10% of primary modal mass)\nConstraint: absorber mass must fit spatially and not degrade primary function"],
        ["Tuned Mass Damper (TMD)", "DVA with optimised damping for broadband or random excitation\nUsed in tall buildings (Taipei 101: 660-tonne TMD), bridges, chimneys\nBridge TMD: reduces wind-induced galloping and pedestrian resonance\nAlternatives: pendulum TMD, liquid column damper (TLCD)"],
        ["Detuning sensitivity", "DVA effectiveness drops sharply if actual frequency ≠ design frequency\nDetuning of ±5%: peak may rise to 2–3× original resonance\nSolution: broadband (damped) DVA or semi-active adaptive DVA\nTemperature changes k₂ of rubber spring → periodic retuning may be needed"],
        ["Multiple-mode DVA", "One DVA targets one mode — add multiple DVAs for multiple modes\nAlternative: multi-frequency DVA (branched spring network)\nDistributed DVA: many small absorbers distributed across structure\nOptimisation: minimise total mass subject to all modal vibration constraints"],
      ]} />

      <h2 style={S.h2}>Active Vibration Control</h2>
      <p style={S.p}>
        Active vibration control overcomes the fundamental limitations of passive isolation — particularly at low frequencies and in the region near the isolator resonance — by using powered actuators driven by a control algorithm. The approach either exploits a reference signal correlated with the disturbance (feedforward) or uses the error output alone (feedback), or combines both.
      </p>
      <DiagramActiveControl />
      <Table2 rows={[
        ["Feedforward control (FxLMS)", "Requires reference signal x correlated with disturbance d\nAdaptive FIR filter W(z) updated by Filtered-X LMS algorithm\nWeight update: W(n+1) = W(n) − 2μ·e(n)·x'(n)  (x' = x filtered through secondary path estimate)\nConverges to optimal W that minimises E[e²(n)] — zero steady-state error for periodic disturbances"],
        ["Feedback control", "No reference needed — only error signal e = r − y\nController C(s) designed for stability (PM > 30°, GM > 6 dB)\nSky-hook damping: F_act = −c_sky·ẋ_abs (velocity of payload)\nPPF (Positive Position Feedback): adds virtual stiffness/damping to collocated sensor-actuator pairs"],
        ["Bode integral constraint", "∫₋∞^∞ ln|S(jω)| dω = π Σ Re(pₖ) ≥ 0  (for open-loop stable plants)\nConsequence: you cannot reduce sensitivity at all frequencies\nWater-bed effect: suppressing vibration in one band → amplification in another\nAVC design must ensure amplification occurs in non-critical frequency range"],
        ["Collocated control", "Sensor and actuator at same physical location\nGuaranteed stability: poles and zeros interlace on imaginary axis\nPPF and direct velocity feedback are always stable for collocated systems\nNon-collocated: non-minimum phase zeros possible → limited bandwidth"],
        ["Actuator technologies", "Voice coil (Lorentz): linear, broadband, low force (0.1–1000 N)\nPiezoelectric stack: high force (kN), very fast (μs), small displacement\nHydraulic: very high force, low bandwidth (~100 Hz)\nElectrodynamic inertial: proof mass on spring-coil, no stiff connection needed"],
        ["Sensor technologies for AVC", "Accelerometer (feedforward reference or error): collocated velocity via integration\nGeophone (velocity): very sensitive below 1 Hz, used in seismic isolation\nForce sensor (collocated with actuator): direct velocity feedback guaranteed stable\nLaser vibrometer: non-contact, no mass loading, very high sensitivity"],
        ["Practical AVC considerations", "Delay: DSP, amplifier, and actuator delays reduce PM → limit bandwidth\nModel uncertainty: uncertain plant G(s) → robust controller needed (H∞, μ-synthesis)\nSaturation: actuator stroke/force limits → performance degrades at large amplitudes\nFail-safe: AVC must revert to passive behaviour on electronics failure"],
      ]} />

      <h2 style={S.h2}>Multi-DOF Isolation Systems</h2>
      <p style={S.p}>
        Real machines and payloads have six degrees of freedom. An isolation system must control all six — the three translational modes (heave, surge, sway) and the three rotational modes (roll, pitch, yaw). Failure to account for one mode can render the entire installation ineffective if that mode is excited by the disturbance.
      </p>
      <DiagramMultiDOF />
      <Table2 rows={[
        ["6-DOF rigid-body dynamics", "Free payload: 6 rigid-body modes from 4 (or more) isolators\nUncoupled frequencies: f_heave=(1/2π)√(ΣkᵢV/M); f_roll=(1/2π)√(ΣkᵢV·xᵢ²/Iₓ)\nAll 6 mode frequencies must be < f_disturb/√2 for isolation to be effective"],
        ["Coupling condition", "Translation-rotation coupling occurs when elastic centre ≠ centre of mass\nElastic centre: weighted centroid of isolator stiffnesses\nCoincidence of elastic centre and CoM → fully decoupled 6 SDOF equations\nIn practice: near-coincidence (< 10% dimension offset) is acceptable"],
        ["Isolator layout rules", "Symmetric layout about CoM in both plan and elevation → minimises coupling\nCorner mounts: highest moment arm → efficient for rocking resistance\nHeight of mounts: raise mounts above CoM → rock frequency increases (often desirable)\nUsually 3–4 mounts minimum (statically determinate with 4)"],
        ["Vertical vs. horizontal isolation", "Vertical (heave): f_n = (1/2π)√(k_v_total/M); most commonly specified\nHorizontal: f_n_h = (1/2π)√(k_h_total/M); k_h < k_v for most isolators\nDesign both: disturbances often have horizontal components too (floor vibration)"],
        ["Rocking modes", "Platform can rock (roll/pitch) about horizontal axes through isolator contact points\nf_rock = (1/2π)√(k_v·L²/I); L = half-span between mounts; I = moment of inertia\nOften f_rock < f_heave → rocking is the critical (hardest to isolate) mode\nSolution: increase span L or raise CoM relative to mount plane"],
        ["Optical table example", "Honeycomb core for high stiffness-to-mass ratio (avoids table resonances)\nSupported on 4 pneumatic isolators at corners\nf_n(vertical) ≈ 1–3 Hz; f_n(horizontal) ≈ 0.5–2 Hz\nAttenuation > 50 dB above 10 Hz — floor vibration effectively eliminated"],
      ]} />

      <h2 style={S.h2}>Damping Treatments</h2>
      <p style={S.p}>
        When isolation between a source and receiver is not feasible — for example, when they must be rigidly connected, or when structural resonances radiate noise — the alternative is to add damping directly to the structure. Damping treatments convert vibrational energy to heat within a viscoelastic material layer.
      </p>
      <DiagramDampingTreatments />
      <Table2 rows={[
        ["Viscoelastic materials", "Complex modulus E* = E'(1+jη) where η = loss factor\nE' (storage modulus): elastic component; η (loss factor): damping component\nBoth E' and η are strong functions of temperature and frequency\nOperating point must match design conditions for effectiveness"],
        ["Free-layer treatment", "Viscoelastic layer bonded to one side of base structure\nDissipation mechanism: extension/compression of VE layer during plate bending\nη_eff ≈ η_v·(E_v/E_b)·(H_v/H_b)³  (stiffness and thickness ratio)\nEffective but requires thick, heavy treatment to be significant"],
        ["Constrained-layer damping (CLD)", "Three-layer sandwich: base / VE / constraining layer\nDissipation via SHEAR of VE interlayer — much more efficient than extension\nη_eff can reach 0.1–0.5 with only 1–3% added mass\nWidely used: car bodies, aerospace panels, ship hulls, printed circuit boards"],
        ["CLD design parameters", "Optimal VE shear modulus G* depends on frequency and temperature\nShear parameter g = E_c·h_c²/(G_v·h_v·L²): relates bending stiffness to VE shear stiffness\nMaximum η_eff at g ≈ 1 (resonance condition in the shear parameter)\nCLD coverage: full coverage optimal; partial coverage uses ≥ 50% of high-strain area"],
        ["Temperature effects on CLD", "Viscoelastic η peaks at glass transition temperature T_g\nBelow T_g: material glassy and stiff — low damping\nAbove T_g: material rubbery and soft — also low damping\nMaterial selection: choose T_g 20–30°C above operating temperature for peak η"],
        ["Particle damping", "Free granular material (metal shot, sand) in cavity within structure\nDissipation via impact and friction between particles and cavity walls\nTemperature-independent, robust to environment\nUsed in spacecraft, cutting tools, sports equipment (tennis rackets, skis)"],
        ["Impact dampers", "Single mass in cavity; impacts with walls — highly nonlinear\nEffective at high amplitudes; ineffective at small oscillation\nTuned particle damper: particles tuned to one dominant frequency\nLow cost, no maintenance, wide temperature range"],
      ]} />

      <h2 style={S.h2}>Control Strategy Comparison</h2>
      <p style={S.p}>
        The three broad strategies — passive, semi-active, and active — are not mutually exclusive. The best practical systems often combine passive isolation (for high-frequency) with an active or semi-active layer (for low-frequency resonance control) in a hybrid architecture that achieves the best of both approaches.
      </p>
      <DiagramStrategyComparison />
      <Table2 rows={[
        ["Hybrid isolation systems", "Passive + active in series or parallel: common architecture for precision systems\nPassive layer handles high-frequency (above ~10 Hz) — removes high-bandwidth demand from actuators\nActive layer handles low-frequency (<10 Hz) where passive isolation is ineffective\nExample: passive air spring + voice coil actuator in parallel (optical table systems)"],
        ["Sky-hook damping", "Virtual damper connected between payload and inertial space (sky)\nF_act = −c_sky·ẋ_abs  (requires absolute velocity measurement)\nModifies transmissibility: T = √[(1+(2ζ_eff·r)²) / ((1−r²)²+(2ζ_eff·r)²)]\nReduces resonance peak without degrading high-freq isolation — breaks passive trade-off"],
        ["Adaptive-passive systems", "Adjust stiffness k or damping c in real time (semi-active)\nVariable orifice hydraulic mount: change flow restriction → change c(t)\nMR damper: change current I → change yield stress → change c(t)\nCannot exceed energy dissipation (no power injection) — bounded performance"],
        ["Active-passive trade-off", "Active: can isolate below own resonance frequency; limited by actuator bandwidth\nPassive: no power, no instability risk, effective above √2·fₙ\nActive wins at: very low frequencies, broadband attenuation, tight specs\nPassive wins at: high frequency, harsh environments, no power available"],
        ["Structural Health Monitoring (SHM)", "Vibration data monitored continuously → changes in fₙ indicate damage\nChange in fₙ ∝ −(1/2)·Δk/k  (stiffness loss → lower frequency)\nCombined: isolation reduces externally induced vibration so structural changes are detectable\nSensors for isolation often reused for SHM — integrated system"],
      ]} />

      <h2 style={S.h2}>Practical Design Process</h2>
      <p style={S.p}>
        Vibration isolation design is an iterative process that moves from disturbance characterisation, through analytical design, to hardware selection, and finally to experimental validation. The flowchart below summarises the key decision points.
      </p>
      <DiagramDesignProcess />
      <Table2 rows={[
        ["Disturbance characterisation", "Measure or calculate the frequency, amplitude, and direction of all vibration sources\nIdentify dominant harmonics (1×, 2× RPM, blade-pass frequency)\nDetermine if disturbance is tonal, broadband, or transient (shock)\nSpecify maximum allowable transmissibility T_max at each critical frequency"],
        ["Isolator sizing", "Choose ωₙ: ωₙ ≤ ω_min_disturbance / 3  (conservative)\nCompute required stiffness: k = M·ωₙ²  (total, then distribute among mounts)\nCheck static deflection: δ_st = g/ωₙ² — must be mechanically feasible\nVerify that mount stiffness provides adequate stability under all operating loads"],
        ["Damping selection strategy", "For machinery (pump, motor): ζ ≈ 0.1–0.2 (control startup resonance crossing)\nFor precision equipment (microscope): ζ < 0.05 (minimise T at high frequency)\nFor shock-prone environments: ζ ≈ 0.2–0.4 or add separate shock absorber"],
        ["Validation testing", "Measure transmissibility FRF with load applied (operational conditions)\nCompare measured fₙ to design target — adjust preload if necessary\nMeasure T at all critical frequencies — must meet specification\nCheck for rocking modes and cross-axis coupling — should be at designed frequencies"],
        ["Standards and regulations", "ISO 10846: dynamic stiffness of resilient mountings (insertion loss)\nISO 2631: whole-body vibration exposure limits for humans\nASHRAE 68: vibration criteria for sensitive equipment rooms (VC curves)\nNASA-HDBK-7005: dynamic environmental criteria for spacecraft\nANSI S2.41: mechanical vibration of shipboard equipment"],
      ]} />

      <InfoBox title="Key Formulae Summary" items={[
        "Transmissibility: T = √[(1+(2ζr)²)/((1−r²)²+(2ζr)²)]  where r=ω/ωₙ;  isolation for r > √2",
        "Isolator: ωₙ = √(k/m);  δ_static = g/ωₙ²;  c = 2ζ√(km);  k = Mωₙ²",
        "DVA Den Hartog: f_opt = 1/(1+μ);  ζ_opt = √(3μ/[8(1+μ)³]);  μ = m_DVA/M_primary",
        "Sky-hook: F_act = −c_sky·ẋ_abs  — adds damping without degrading isolation slope",
        "CLD loss factor: η_eff via shear parameter g = E_c·h_c²/(G_v·h_v·L²);  maximised at g≈1",
        "FxLMS update: W(n+1) = W(n) − 2μ·e(n)·x'(n)  (x' = reference filtered through secondary path)",
        "6-DOF: f_heave=(1/2π)√(Σk_vi/M);  f_rock=(1/2π)√(Σk_vi·xᵢ²/I);  all must be < f_disturb/√2",
        "Bode integral: ∫ln|S(jω)|dω ≥ 0 — feedback AVC cannot reduce T everywhere simultaneously",
      ]} />
    </div>
  );
}